import { motion } from 'framer-motion';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui';
import { ActionBar } from '@/features/feed/components/ActionBar';
import { calculateReadingTime, formatTimeAgo, getThemeLabel } from '@/features/feed/utils';
import type { FeedItem } from '@/types';

interface ContentCardProps {
  item: FeedItem;
  onLike: (contentId: string, isLiked: boolean) => void;
  onSave: (contentId: string, isSaved: boolean) => void;
  onDismiss: (contentId: string) => void;
  onMore: (contentId: string, action: 'feedback' | 'muteAuthor' | 'muteSource') => void;
}

export const ContentCard = memo(function ContentCard({
  item,
  onLike,
  onSave,
  onDismiss,
  onMore,
}: ContentCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { content, tier, userState } = item;
  const isPrimary = tier === 'primary';

  const handleCardClick = useCallback(() => {
    navigate(`/content/${content.id}`, { state: { origin: 'feed' } });
  }, [navigate, content.id]);

  const handleLike = useCallback(() => {
    onLike(content.id, userState.isLiked);
  }, [content.id, userState.isLiked, onLike]);

  const handleSave = useCallback(() => {
    onSave(content.id, userState.isSaved);
  }, [content.id, userState.isSaved, onSave]);

  const handleDismiss = useCallback(() => {
    onDismiss(content.id);
  }, [content.id, onDismiss]);

  const handleMore = useCallback(
    (action: 'feedback' | 'muteAuthor' | 'muteSource') => {
      onMore(content.id, action);
    },
    [content.id, onMore],
  );

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      layout
      onClick={handleCardClick}
      className={`rounded-lg p-6 transition-shadow bg-white cursor-pointer ${
        isPrimary ? 'shadow-md hover:shadow-lg' : 'shadow-sm hover:shadow-md'
      }`}
    >
      {/* Theme Tag */}
      <Badge variant="theme" className="mb-3">
        {getThemeLabel(content.theme, t)}
      </Badge>

      {/* Headline */}
      <h2
        className={`font-serif font-semibold text-[#2E2A23] leading-tight hover:text-[#E94E1B] transition-colors ${
          isPrimary ? 'text-xl md:text-2xl' : 'text-lg md:text-xl'
        }`}
      >
        {content.title}
      </h2>

      {/* Summary */}
      <p
        className={`mt-3 text-[#888185] leading-relaxed ${
          isPrimary ? 'line-clamp-3' : 'line-clamp-2'
        }`}
      >
        {content.summary}
      </p>

      {/* Metadata */}
      <div className="mt-4 flex items-center gap-2 text-sm text-[#888185]">
        <span className="font-medium">{content.author}</span>
        <span>·</span>
        <span>{formatTimeAgo(content.publishedAt, t)}</span>
        <span>·</span>
        <span>
          {t('content.readingTime', {
            minutes: content.readingTimeMinutes || calculateReadingTime(content.body),
          })}
        </span>
      </div>

      {/* Actions - stop propagation to prevent card click */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: stopPropagation wrapper for action bar */}
      <div role="presentation" onClick={(e) => e.stopPropagation()}>
        <ActionBar
          contentId={content.id}
          isLiked={userState.isLiked}
          isSaved={userState.isSaved}
          showMore={isPrimary}
          onLike={handleLike}
          onSave={handleSave}
          onDismiss={handleDismiss}
          onMore={handleMore}
          className="mt-4"
        />
      </div>
    </motion.article>
  );
});
